/*
 * /massage-sessions/$patientId (page)
 */
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Page,
	Placeholder,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { CalendarDays, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { trpc } from "../../trpc"
import { PatientHeader } from "../../features/patient/patient-header"
import { rolePageLoader } from "../../utils/auth.utils"
import type { Patient } from "@emr/db/client"

const MOCK_PATIENT: Partial<Patient> = {
	firstName: "Patient",
	lastName: "",
}

export const Route = createFileRoute("/massage-sessions/$patientId")({
	component: MassageSessionsPage,
	loader: rolePageLoader(["massage_therapist", "admin", "super_admin"]),
})

function MassageSessionsPage() {
	const { patientId } = Route.useParams()

	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { id: patientId } })
	const { data: practitioners = [] } = trpc.practitioner.getAllByFilter.useQuery({ where: { active: true } })

	const {
		data: sessions = [],
		isLoading: sessionsLoading,
		refetch: refetchSessions,
	} = trpc.massageSession.getAllForPatient.useQuery({ patientId })

	const {
		data: approvals = [],
		isLoading: approvalsLoading,
		refetch: refetchApprovals,
	} = trpc.massageSessionApproval.getAllForPatient.useQuery({ patientId })

	const { data: sessionsData, refetch: refetchRemaining } =
		trpc.massageSession.getSessionsRemaining.useQuery({ patientId })

	const displayPatient = patient ?? (MOCK_PATIENT as Patient)

	function refreshAll() {
		refetchSessions()
		refetchApprovals()
		refetchRemaining()
	}

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={false} />
			<Container width="xl" padding="lg" gap="lg">
				{/* Sessions summary */}
				{sessionsData && (
					<div className="flex items-center gap-6 p-4 rounded-xl border bg-muted/40 text-sm">
						<div className="flex flex-col">
							<span className="text-xs text-muted-foreground uppercase tracking-wide">Approved</span>
							<span className="text-2xl font-bold">{sessionsData.approved}</span>
						</div>
						<div className="flex flex-col">
							<span className="text-xs text-muted-foreground uppercase tracking-wide">Completed</span>
							<span className="text-2xl font-bold">{sessionsData.completed}</span>
						</div>
						<div className="flex flex-col">
							<span className="text-xs text-muted-foreground uppercase tracking-wide">Remaining</span>
							<span className={`text-2xl font-bold ${sessionsData.remaining <= 3 ? "text-destructive" : ""}`}>
								{sessionsData.remaining}
							</span>
						</div>
					</div>
				)}

				<div className="grid gap-6 lg:grid-cols-2">
					{/* VAC Approvals */}
					<VacApprovalsSection
						patientId={patientId}
						approvals={approvals as any[]}
						loading={approvalsLoading}
						onChanged={refreshAll}
					/>

					{/* Register new session */}
					<NewSessionForm
						patientId={patientId}
						practitioners={practitioners as any[]}
						onCreated={refreshAll}
					/>
				</div>

				{/* Session history */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Session History</CardTitle>
					</CardHeader>
					<CardContent>
						{sessionsLoading ? (
							<Placeholder title="Loading sessions…" />
						) : !(sessions as any[]).length ? (
							<Placeholder title="No sessions recorded yet" />
						) : (
							<div className="space-y-3">
								{(sessions as any[]).map((s) => (
									<SessionCard
										key={s.id}
										session={s}
										onDeactivated={refreshAll}
									/>
								))}
							</div>
						)}
					</CardContent>
				</Card>
			</Container>
		</Page>
	)
}

/* ─── VAC Approvals section ─────────────────────────────────────────────── */

function VacApprovalsSection({
	patientId,
	approvals,
	loading,
	onChanged,
}: {
	patientId: string
	approvals: any[]
	loading: boolean
	onChanged: () => void
}) {
	const [adding, setAdding] = useState(false)
	const [dateApproved, setDateApproved] = useState("")
	const [sessionsApproved, setSessionsApproved] = useState("")
	const [notes, setNotes] = useState("")

	const createApproval = trpc.massageSessionApproval.create.useMutation({
		onSuccess: () => {
			setAdding(false)
			setDateApproved("")
			setSessionsApproved("")
			setNotes("")
			onChanged()
			toast.success("VAC approval added")
		},
		onError: (e) => toast.error(e.message),
	})

	function handleAdd() {
		if (!dateApproved || !sessionsApproved) return
		createApproval.mutate({
			patientId,
			dateApproved,
			sessionsApproved: parseInt(sessionsApproved, 10),
			notes: notes.trim() || undefined,
		})
	}

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between pb-2">
				<CardTitle className="text-base">VAC Approvals</CardTitle>
				{!adding && (
					<Button size="sm" variant="outline" onClick={() => setAdding(true)}>
						<Plus className="w-3.5 h-3.5 mr-1" /> Add
					</Button>
				)}
			</CardHeader>
			<CardContent className="space-y-3">
				{adding && (
					<div className="rounded-lg border bg-muted/40 p-3 space-y-2">
						<div className="grid grid-cols-2 gap-2">
							<div>
								<label className="text-xs text-muted-foreground">Date Approved</label>
								<input
									type="date"
									value={dateApproved}
									onChange={(e) => setDateApproved(e.target.value)}
									className="w-full h-8 rounded-md border bg-background px-2 text-sm mt-0.5"
								/>
							</div>
							<div>
								<label className="text-xs text-muted-foreground"># Sessions Approved</label>
								<input
									type="number"
									min="1"
									value={sessionsApproved}
									onChange={(e) => setSessionsApproved(e.target.value)}
									className="w-full h-8 rounded-md border bg-background px-2 text-sm mt-0.5"
								/>
							</div>
						</div>
						<div>
							<label className="text-xs text-muted-foreground">Notes</label>
							<input
								type="text"
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								placeholder="e.g. VAC file reference"
								className="w-full h-8 rounded-md border bg-background px-2 text-sm mt-0.5"
							/>
						</div>
						<div className="flex gap-2 pt-1">
							<Button
								size="sm"
								onClick={handleAdd}
								disabled={createApproval.isPending || !dateApproved || !sessionsApproved}
							>
								{createApproval.isPending ? "Saving…" : "Save"}
							</Button>
							<Button size="sm" variant="outline" onClick={() => setAdding(false)}>
								Cancel
							</Button>
						</div>
					</div>
				)}
				{loading ? (
					<Placeholder title="Loading…" />
				) : !approvals.length ? (
					<Placeholder title="No VAC approvals on file" />
				) : (
					approvals.map((a) => (
						<div key={a.id} className="rounded-lg border px-3 py-2 text-sm flex items-start justify-between gap-2">
							<div className="space-y-0.5">
								<p className="font-medium">
									{new Date(a.dateApproved).toLocaleDateString("en-CA", {
										year: "numeric", month: "short", day: "numeric",
									})}
								</p>
								<p className="text-muted-foreground">
									{a.sessionsApproved} session{a.sessionsApproved !== 1 ? "s" : ""} approved
								</p>
								{a.notes && <p className="text-xs text-muted-foreground">{a.notes}</p>}
							</div>
						</div>
					))
				)}
			</CardContent>
		</Card>
	)
}

/* ─── New Session Form ───────────────────────────────────────────────────── */

function NewSessionForm({
	patientId,
	practitioners,
	onCreated,
}: {
	patientId: string
	practitioners: any[]
	onCreated: () => void
}) {
	const [open, setOpen] = useState(false)
	const [sessionDate, setSessionDate] = useState("")
	const [startTime, setStartTime] = useState("")
	const [endTime, setEndTime] = useState("")
	const [therapistId, setTherapistId] = useState("")
	const [notes, setNotes] = useState("")

	const createSession = trpc.massageSession.create.useMutation({
		onSuccess: () => {
			setOpen(false)
			setSessionDate("")
			setStartTime("")
			setEndTime("")
			setNotes("")
			onCreated()
			toast.success("Session recorded")
		},
		onError: (e) => toast.error(e.message),
	})

	function localDateTimeToISO(dateStr: string, timeStr: string): string | undefined {
		if (!dateStr || !timeStr) return undefined
		return new Date(`${dateStr}T${timeStr}`).toISOString()
	}

	function handleSubmit() {
		if (!sessionDate) return
		createSession.mutate({
			patientId,
			therapistId: therapistId || undefined,
			sessionDate,
			startTime: localDateTimeToISO(sessionDate, startTime),
			endTime: localDateTimeToISO(sessionDate, endTime),
			notes: notes.trim() || undefined,
		})
	}

	return (
		<Card>
			<CardHeader
				className="flex flex-row items-center justify-between pb-2 cursor-pointer"
				onClick={() => setOpen((v) => !v)}
			>
				<CardTitle className="text-base">Register New Session</CardTitle>
				{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
			</CardHeader>
			{open && (
				<CardContent className="space-y-3">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<div>
							<label className="text-xs text-muted-foreground">Session Date *</label>
							<input
								type="date"
								value={sessionDate}
								onChange={(e) => setSessionDate(e.target.value)}
								className="w-full h-8 rounded-md border bg-background px-2 text-sm mt-0.5"
							/>
						</div>
						<div>
							<label className="text-xs text-muted-foreground">Therapist</label>
							<select
								value={therapistId}
								onChange={(e) => setTherapistId(e.target.value)}
								className="w-full h-8 rounded-md border bg-background px-2 text-sm mt-0.5"
							>
								<option value="">— Select therapist —</option>
								{practitioners.map((p) => (
									<option key={p.id} value={p.id}>
										{p.firstName} {p.lastName}
									</option>
								))}
							</select>
						</div>
						<div>
							<label className="text-xs text-muted-foreground">Start Time</label>
							<input
								type="time"
								value={startTime}
								onChange={(e) => setStartTime(e.target.value)}
								className="w-full h-8 rounded-md border bg-background px-2 text-sm mt-0.5"
							/>
						</div>
						<div>
							<label className="text-xs text-muted-foreground">End Time</label>
							<input
								type="time"
								value={endTime}
								onChange={(e) => setEndTime(e.target.value)}
								className="w-full h-8 rounded-md border bg-background px-2 text-sm mt-0.5"
							/>
						</div>
					</div>
					<div>
						<label className="text-xs text-muted-foreground">Session Notes (also saved as medical note)</label>
						<textarea
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							rows={4}
							placeholder="Describe the session, areas treated, patient response…"
							className="w-full rounded-md border bg-background px-3 py-2 text-sm mt-0.5 resize-none"
						/>
					</div>
					<div className="flex gap-2 pt-1">
						<Button
							size="sm"
							onClick={handleSubmit}
							disabled={createSession.isPending || !sessionDate}
						>
							{createSession.isPending ? "Saving…" : "Record Session"}
						</Button>
						<Button size="sm" variant="outline" onClick={() => setOpen(false)}>
							Cancel
						</Button>
					</div>
				</CardContent>
			)}
		</Card>
	)
}

/* ─── Session card ──────────────────────────────────────────────────────── */

function SessionCard({ session, onDeactivated }: { session: any; onDeactivated: () => void }) {
	const deactivate = trpc.massageSession.deactivate.useMutation({
		onSuccess: () => {
			onDeactivated()
			toast.success("Session removed")
		},
		onError: (e) => toast.error(e.message),
	})

	const dateStr = session.sessionDate
		? new Date(session.sessionDate).toLocaleDateString("en-CA", {
				year: "numeric", month: "short", day: "numeric",
			})
		: "—"

	const timeRange =
		session.startTime && session.endTime
			? `${fmtTime(session.startTime)} – ${fmtTime(session.endTime)}`
			: session.startTime
				? `From ${fmtTime(session.startTime)}`
				: null

	return (
		<div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm space-y-1.5">
			<div className="flex items-start justify-between gap-2">
				<div className="flex items-center gap-2 font-medium">
					<CalendarDays className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
					{dateStr}
					{timeRange && <span className="text-muted-foreground font-normal text-xs">({timeRange})</span>}
				</div>
				<Button
					size="sm"
					variant="ghost"
					className="h-6 px-1.5 text-muted-foreground hover:text-destructive shrink-0"
					onClick={() => deactivate.mutate({ id: session.id })}
					disabled={deactivate.isPending}
				>
					<Trash2 className="w-3.5 h-3.5" />
				</Button>
			</div>
			{session.therapistName && (
				<p className="text-xs text-muted-foreground">{session.therapistName}</p>
			)}
			{session.notes && (
				<p className="text-muted-foreground whitespace-pre-wrap">{session.notes}</p>
			)}
		</div>
	)
}

function fmtTime(d: Date | string) {
	return new Date(d).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })
}
